import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LucideIcon, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
interface ProductCardProps {
  title: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
  accent?: 'sage' | 'indigo';
  className?: string;
  delay?: number;
  shapeType?: 'circle' | 'square' | 'diamond' | 'hexagon';
}
export const ProductCard = ({
  title,
  tagline,
  description,
  icon: Icon,
  accent = 'sage',
  className,
  delay = 0,
  shapeType = 'square'
}: ProductCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const renderShape = () => {
    const color = accent === 'sage' ? "text-sage-600" : "text-indigo-500";
    switch (shapeType) {
      case 'circle':
        return <circle cx="50" cy="50" r="45" fill="currentColor" className={color} data-id="element-1027" />;
      case 'diamond':
        return <polygon points="50,5 90,50 50,95 10,50" fill="currentColor" className={color} data-id="element-1028" />;
      case 'hexagon':
        return <polygon points="25,10 75,10 90,50 75,90 25,90 10,50" fill="currentColor" className={color} data-id="element-1029" />;
      case 'square':
      default:
        return <rect x="10" y="10" width="80" height="80" rx="20" fill="currentColor" className={color} data-id="element-1030" />;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: "-100px"
  }} transition={{
    duration: 0.5,
    delay: delay * 0.1,
    ease: [0.21, 0.47, 0.32, 0.98]
  }} className={cn(className)} data-id="element-1031">
      <Card className={cn("group relative h-full overflow-hidden border-0 bg-white/90 backdrop-blur-sm transition-all duration-300 cursor-pointer", "hover:shadow-2xl hover:-translate-y-1 hover:bg-white", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600", 'p-8')} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={() => {
      // Navigate to early access page - assuming it's a /waitlist route
      window.location.href = '/waitlist';
    }} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        window.location.href = '/waitlist';
      }
    }} role="button" tabIndex={0} aria-label={`Learn more about ${title} - Get early access`} data-id="element-1032">
        {/* Gradient overlay on hover */}
        <div className={cn("absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100", accent === 'sage' ? "bg-gradient-to-br from-sage-600/5 via-transparent to-sage-700/5" : "bg-gradient-to-br from-indigo-500/5 via-transparent to-indigo-600/5")} data-id="element-1033" />
        
        {/* Geometric accent shape */}
        <motion.div className="absolute -right-20 -top-20 h-40 w-40 pointer-events-none" animate={{
        rotate: isHovered ? 180 : 0,
        scale: isHovered ? 1.1 : 1
      }} transition={{
        duration: 0.8,
        ease: "easeInOut"
      }} data-id="element-1034">
          <svg viewBox="0 0 100 100" className="h-full w-full opacity-5" data-id="element-1035">
            {renderShape()}
          </svg>
        </motion.div>
        
        {/* Icon with animation */}
        <div className="relative mb-6" data-id="element-1036">
          <motion.div className={cn("inline-flex h-14 w-14 items-center justify-center rounded-2xl", accent === 'sage' ? "bg-sage-600/10" : "bg-indigo-500/10")} whileHover={{
          scale: 1.05,
          rotate: 5
        }} transition={{
          type: "spring",
          stiffness: 400,
          damping: 15
        }} data-id="element-1037">
            <Icon className={cn("h-7 w-7", accent === 'sage' ? "text-sage-600" : "text-indigo-500")} data-id="element-1038" />
          </motion.div>
        </div>
        
        {/* Content */}
        <div className="relative space-y-4" data-id="element-1039">
          <div data-id="element-1040">
            <h3 className="font-display font-bold tracking-tight text-slate-900 text-2xl" data-id="element-1041">
              {title}
            </h3>
            <p className={cn("mt-2 font-medium text-sm", accent === 'sage' ? "text-sage-600" : "text-indigo-500")} data-id="element-1042">
              {tagline}
            </p>
          </div>
          
          <p className="text-muted-foreground leading-relaxed text-sm" data-id="element-1043">
            {description}
          </p>
          
          {/* Hover indicator */}
          <motion.div className="flex items-center gap-2 text-sm font-medium" initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: isHovered ? 1 : 0,
          x: isHovered ? 0 : -10
        }} transition={{
          duration: 0.2
        }} data-id="element-1044">
            <span className={accent === 'sage' ? "text-sage-600" : "text-indigo-500"} data-id="element-1045">
              Learn more
            </span>
            <ArrowRight className="h-4 w-4" data-id="element-1046" />
          </motion.div>
        </div>
      </Card>
    </motion.div>;
};